import React, { useState } from "react";
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Tooltip, Typography, Box, Menu, MenuItem,
    ListItemIcon, ListItemText, Divider, CircularProgress
} from "@mui/material";
import FolderIcon from "@mui/icons-material/Folder";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import AudioFileIcon from "@mui/icons-material/AudioFile";
import DownloadIcon from "@mui/icons-material/Download";
import FolderZipIcon from "@mui/icons-material/FolderZip";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import ArticleIcon from "@mui/icons-material/Article";
import SubjectIcon from "@mui/icons-material/Subject";
import ImageIcon from "@mui/icons-material/Image";
import VideocamIcon from "@mui/icons-material/Videocam";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/EditOutlined";
import CloudSyncIcon from "@mui/icons-material/CloudSync";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import DriveFileMoveIcon from "@mui/icons-material/DriveFileMove";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import OpenInBrowserIcon from "@mui/icons-material/OpenInBrowser";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import dayjs from "dayjs";
import { downloadDriveItemZip } from "../../services/drive.service";
import {
    exportMeetingSummaryPdf,
    exportTranscriptPdf,
    exportFileNotePdf,
} from "../../services/meetings.service";

async function saveBlobToFile(blob: Blob, suggestedName: string) {
    if ("showSaveFilePicker" in window) {
        try {
            const handle = await (window as any).showSaveFilePicker({ suggestedName });
            const writable = await handle.createWritable();
            await writable.write(blob);
            await writable.close();
            return;
        } catch (e: any) {
            if (e.name === "AbortError") return;
        }
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = suggestedName;
    a.click();
    URL.revokeObjectURL(url);
}

type SortField = "name" | "updatedAt" | "size" | null;
type SortDir = "asc" | "desc";

/**
 * A file or folder that lives in an advisor's own OneDrive and is only
 * referenced by Prospo. Prospo must never write to these, so the mutating
 * actions are withheld for them.
 */
const isOneDriveLink = (item: any): boolean => item?.storageProvider === "ONEDRIVE_LINK";

interface FileTableProps {
    items: any[];
    onNavigate: (folderId: string) => void;
    onRefresh: () => void;
    onAction: (
        action: "rename" | "move" | "delete" | "preview" | "openInBrowser" | "unlinkOneDrive" | "syncOneDrive",
        item: any,
    ) => void;
    onItemClick: (item: any) => void;
    /**
     * A linked folder only ever attaches at the client root, so that is the one
     * place "Remove link" makes sense. Deeper down, a linked folder is a child
     * mirrored by the sync and unlinking it would simply be undone on the next
     * refresh of its parent.
     */
    isAtClientRoot?: boolean;
}

export default function FileTable({ items, onNavigate, onRefresh, onAction, onItemClick, isAtClientRoot = false }: FileTableProps) {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [activeItem, setActiveItem] = useState<any>(null);
    const [downloadingId, setDownloadingId] = useState<string | null>(null);
    const [exportingPdfId, setExportingPdfId] = useState<string | null>(null);
    const menuItemRef = React.useRef<any>(null);

    const [sortField, setSortField] = useState<SortField>(null);
    const [sortDir, setSortDir] = useState<SortDir>("asc");

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, item: any) => {
        event.stopPropagation();
        menuItemRef.current = item;
        setAnchorEl(event.currentTarget);
        setActiveItem(item);
    };

    const handleMenuClose = (event?: React.MouseEvent) => {
        if (event && event.stopPropagation) event.stopPropagation();
        setAnchorEl(null);
        setTimeout(() => {
            setActiveItem(null);
            menuItemRef.current = null;
        }, 300);
    };

    const handleActionClick = (event: React.MouseEvent, action: Parameters<FileTableProps["onAction"]>[0]) => {
        event.stopPropagation();
        onAction(action, activeItem);
        handleMenuClose();
    };

    const handleDownloadFile = (event: React.MouseEvent, item: any) => {
        event.stopPropagation();
        handleMenuClose();
        if (!item.fileUrl) return;
        window.open(item.fileUrl, "_blank");
    };

    const handleExportAsPdf = async (event: React.MouseEvent, item: any) => {
        event.stopPropagation();
        handleMenuClose();
        const entityType: string = item.prospoContext?.entityType || "";
        const entityId: string = item.prospoContext?.entityId || "";
        const meetingId: string = item.prospoContext?.meetingId || "";
        if (!entityType || !meetingId) return;
        if ((entityType === 'TRANSCRIPT' || entityType === 'FILE_NOTE') && !entityId) return;
        setExportingPdfId(item._id);
        try {
            let res: Response;
            if (entityType === "MEETING_SUMMARY") {
                res = await exportMeetingSummaryPdf(meetingId) as Response;
            } else if (entityType === "TRANSCRIPT") {
                res = await exportTranscriptPdf(meetingId, entityId) as Response;
            } else if (entityType === "FILE_NOTE") {
                res = await exportFileNotePdf(meetingId, entityId) as Response;
            } else {
                return;
            }
            if (!res.ok) throw new Error("Export failed");
            const blob = await res.blob();
            const baseName = (item.name || "export").replace(/\.[^.]+$/, "");
            const pdfUrl = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = pdfUrl;
            a.download = `${baseName}.pdf`;
            a.click();
            URL.revokeObjectURL(pdfUrl);
        } catch {
            alert("Failed to export as PDF. Please try again.");
        } finally {
            setExportingPdfId(null);
        }
    };

    const handleDownloadFolderZip = async (event: React.MouseEvent, item: any) => {
        event.stopPropagation();
        handleMenuClose();
        setDownloadingId(item._id);
        try {
                const res = await downloadDriveItemZip(item._id);
            if (!res.ok) throw new Error("Server error");
            const blob = await res.blob();
            await saveBlobToFile(blob, `${item.name}.zip`);
        } catch {
            alert("Failed to download folder. Please try again.");
        } finally {
            setDownloadingId(null);
        }
    };

    // --- SORT ---
    const handleSortClick = (field: SortField) => {
        if (sortField === field) {
            setSortDir(d => d === "asc" ? "desc" : "asc");
        } else {
            setSortField(field);
            setSortDir("asc");
        }
    };

    const hasMeetingFolders = React.useMemo(
        () => items.some((item) => item.prospoContext?.meetingCategory),
        [items]
    );

    const sortedItems = React.useMemo(() => {
        if (!sortField) return items;

        return [...items].sort((a, b) => {
            if (a.isFolder && !b.isFolder) return -1;
            if (!a.isFolder && b.isFolder) return 1;

            let aVal: any, bVal: any;
            if (sortField === "name") {
                aVal = (a.name || "").toLowerCase();
                bVal = (b.name || "").toLowerCase();
            } else if (sortField === "updatedAt") {
                aVal = new Date(a.updatedAt || a.createdAt).getTime();
                bVal = new Date(b.updatedAt || b.createdAt).getTime();
            } else if (sortField === "size") {
                aVal = a.file?.size || 0;
                bVal = b.file?.size || 0;
            }

            if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
            if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
            return 0;
        });
    }, [items, sortField, sortDir]);

    // --- HELPERS ---
    const getFileIcon = (item: any) => {
        if (item.isFolder) return <FolderIcon sx={{ color: "#fbc02d", fontSize: 28 }} />;
        const mimeType = item.file?.mimeType || "";
        const entityType = item.prospoContext?.entityType || "";
        if (entityType === "FILE_NOTE") return <ArticleIcon sx={{ color: "#5c6bc0", fontSize: 28 }} />;
        if (entityType === "MEETING_SUMMARY") return <ArticleIcon sx={{ color: "#7c3aed", fontSize: 28 }} />;
        if (entityType === "TRANSCRIPT") return <SubjectIcon sx={{ color: "#00897b", fontSize: 28 }} />;
        if (entityType === "FACT_FIND" || mimeType.includes("pdf")) return <PictureAsPdfIcon sx={{ color: "#d32f2f", fontSize: 28 }} />;
        if (mimeType.includes("image")) return <ImageIcon sx={{ color: "#43a047", fontSize: 28 }} />;
        if (mimeType.includes("video")) return <VideocamIcon sx={{ color: "#e53935", fontSize: 28 }} />;
        if (mimeType.includes("audio")) return <AudioFileIcon sx={{ color: "#1976d2", fontSize: 28 }} />;
        if (mimeType.includes("word") || mimeType.includes("wordprocessingml")) return <ArticleIcon sx={{ color: "#1565c0", fontSize: 28 }} />;
        return <InsertDriveFileIcon sx={{ color: "#757575", fontSize: 28 }} />;
    };

    const getDisplayName = (item: any): string => {
        if (item.prospoContext?.entityType === "FACT_FIND") {
            return (item.name || "").replace(/\.(json|pdf)$/, ".pdf");
        }
        // Strip trailing date suffix in the format DD-MM-YYYY (e.g. "Meeting on 20-05-2026 20-05-2026")
        return (item.name || "").replace(/\s+\d{2}-\d{2}-\d{4}$/, "");
    };

    const getTypeLabel = (item: any): string => {
        if (item.isFolder) return isOneDriveLink(item) ? "Linked folder" : "Folder";
        const entityType = item.prospoContext?.entityType;
        if (entityType === "FACT_FIND") return "Fact Find";
        if (entityType === "TRANSCRIPT") return "Transcript";
        if (entityType === "FILE_NOTE") return "File Note";
        if (entityType === "MEETING_SUMMARY") return "Meeting Summary";
        return entityType || "File";
    };

    const formatSize = (bytes: number) => {
        if (!bytes || bytes === 0) return "--";
        const k = 1024;
        const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    };

    const SortIcon = ({ field }: { field: SortField }) =>
        sortField === field
            ? sortDir === "asc"
                ? <ArrowUpwardIcon sx={{ fontSize: 13, ml: 0.5 }} />
                : <ArrowDownwardIcon sx={{ fontSize: 13, ml: 0.5 }} />
            : null;

    const SortableHeader = ({ field, label }: { field: SortField; label: string }) => (
        <TableCell
            onClick={() => handleSortClick(field)}
            sx={{
                fontWeight: "bold",
                backgroundColor: "#f5f5f5",
                cursor: "pointer",
                userSelect: "none",
                whiteSpace: "nowrap",
                "&:hover": { backgroundColor: "#ebebeb" }
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center" }}>
                {label}
                <SortIcon field={field} />
            </Box>
        </TableCell>
    );

    return (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
            {sortedItems.length === 0 ? (
                <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Typography color="text.secondary">This folder is empty.</Typography>
                </Box>
            ) : (
                <TableContainer sx={{ flexGrow: 1, overflow: "auto" }}>
                    <Table sx={{ minWidth: 650 }} aria-label="file explorer table" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <SortableHeader field="name" label="Name" />
                                {hasMeetingFolders && <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>Meeting Type</TableCell>}
                                <SortableHeader field="updatedAt" label="Modified" />
                                <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>Type</TableCell>
                                <SortableHeader field="size" label="Size" />
                                <TableCell align="right" sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedItems.map((item) => (
                                <TableRow
                                    key={item._id}
                                    onClick={() => onItemClick(item)}
                                    sx={{
                                        "&:last-child td, &:last-child th": { border: 0 },
                                        cursor: "pointer",
                                        "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                                        transition: "background-color 0.15s"
                                    }}
                                >
                                    <TableCell component="th" scope="row">
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                            {getFileIcon(item)}
                                            <Typography variant="body2" sx={{ fontWeight: item.isFolder ? 500 : 400 }}>
                                                {getDisplayName(item)}
                                            </Typography>
                                            {/* Only the linked folder itself is badged, not every file inside it —
                                                one marker at the boundary reads as a label, a badge on every row
                                                reads as noise. */}
                                            {isOneDriveLink(item) && item.isFolder && (
                                                <Box sx={{ display: "inline-flex", alignItems: "center", px: 1.25, py: 0.25, borderRadius: "12px", backgroundColor: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe", fontSize: "0.7rem", fontWeight: 600 }}>
                                                    OneDrive
                                                </Box>
                                            )}
                                        </Box>
                                    </TableCell>
                                    {hasMeetingFolders && (
                                        <TableCell>
                                            {item.prospoContext?.meetingCategory ? (
                                                (() => {
                                                    const isGeneral = item.prospoContext.meetingCategory === "general";
                                                    const badge = isGeneral
                                                        ? { bg: "#f5f3ff", color: "#6d28d9", border: "1px solid #ddd6fe", label: "General" }
                                                        // Display label only — the stored category value stays "financial".
                                                        : { bg: "#e0f2fe", color: "#0369a1", border: "1px solid #bae6fd", label: "Advice" };
                                                    return (
                                                        <Box sx={{ display: "inline-flex", alignItems: "center", px: 1.5, py: 0.25, borderRadius: "12px", backgroundColor: badge.bg, color: badge.color, border: badge.border, fontSize: "0.75rem", fontWeight: 500 }}>
                                                            {badge.label}
                                                        </Box>
                                                    );
                                                })()
                                            ) : null}
                                        </TableCell>
                                    )}
                                    <TableCell>
                                        {dayjs(item.updatedAt || item.createdAt).format("MMM DD, YYYY HH:mm")}
                                    </TableCell>
                                    <TableCell>
                                        {getTypeLabel(item)}
                                    </TableCell>
                                    <TableCell>{item.isFolder ? "--" : formatSize(item.file?.size)}</TableCell>
                                    <TableCell align="right">
                                        {downloadingId === item._id ? (
                                            <Box sx={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 30, height: 30, mr: 0.5 }}>
                                                <CircularProgress size={20} />
                                            </Box>
                                        ) : (
                                            <>
                                                {!item.isFolder && item.fileUrl && (
                                                    <Tooltip title="Download">
                                                        <IconButton size="small" onClick={(e) => handleDownloadFile(e, item)}>
                                                            <DownloadIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {item.isFolder && (
                                                    <Tooltip title="Download as ZIP">
                                                        <IconButton size="small" onClick={(e) => handleDownloadFolderZip(e, item)}>
                                                            <FolderZipIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </>
                                        )}
                                        <Tooltip title="More Actions">
                                            <IconButton size="small" onClick={(e) => handleMenuOpen(e, item)}>
                                                <MoreVertIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose} onClick={(e) => e.stopPropagation()}>
                {!activeItem?.isFolder && (
                    <>
                        <MenuItem onClick={(e) => handleActionClick(e, "preview")}>
                            <ListItemIcon><OpenInNewIcon fontSize="small" /></ListItemIcon>
                            <ListItemText>Open</ListItemText>
                        </MenuItem>
                        <MenuItem onClick={(e) => handleActionClick(e, "openInBrowser")}>
                            <ListItemIcon><OpenInBrowserIcon fontSize="small" /></ListItemIcon>
                            <ListItemText>Open in Browser</ListItemText>
                        </MenuItem>
                        {activeItem?.fileUrl && (
                            <MenuItem onClick={(e) => handleDownloadFile(e, activeItem)}>
                                <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
                                <ListItemText>Download</ListItemText>
                            </MenuItem>
                        )}
                        {["TRANSCRIPT", "FILE_NOTE", "MEETING_SUMMARY"].includes(activeItem?.prospoContext?.entityType) && (
                            <Tooltip title={activeItem?.prospoContext?.isOrphaned ? "Transcript data is no longer available" : ""}>
                                <span>
                                    <MenuItem
                                        onClick={(e) => handleExportAsPdf(e, activeItem)}
                                        disabled={exportingPdfId === activeItem?._id || !!activeItem?.prospoContext?.isOrphaned}
                                    >
                                        <ListItemIcon>
                                            {exportingPdfId === activeItem?._id
                                                ? <CircularProgress size={16} />
                                                : <PictureAsPdfIcon fontSize="small" />}
                                        </ListItemIcon>
                                        <ListItemText>Export as PDF</ListItemText>
                                    </MenuItem>
                                </span>
                            </Tooltip>
                        )}
                        <Divider />
                    </>
                )}
                {activeItem?.isFolder && (
                    <>
                        <MenuItem onClick={(e) => handleDownloadFolderZip(e, activeItem)}>
                            <ListItemIcon><FolderZipIcon fontSize="small" /></ListItemIcon>
                            <ListItemText>Download as ZIP</ListItemText>
                        </MenuItem>
                        {isOneDriveLink(activeItem) && (
                            <MenuItem onClick={(e) => handleActionClick(e, "syncOneDrive")}>
                                <ListItemIcon><CloudSyncIcon fontSize="small" /></ListItemIcon>
                                <ListItemText>Refresh from OneDrive</ListItemText>
                            </MenuItem>
                        )}
                        <Divider />
                    </>
                )}
                {/* Rename, Move and Delete are withheld for linked items: they live in
                    the advisor's OneDrive, which Prospo does not own and never writes to.
                    Offering them would either fail or, worse, appear to succeed. */}
                {isOneDriveLink(activeItem) ? (
                    /* Files as well as folders: a linked file is a link in its
                       own right, and isAtClientRoot already excludes mirrored
                       content, which lives under the folder it came from. */
                    isAtClientRoot ? (
                        <MenuItem onClick={(e) => handleActionClick(e, "unlinkOneDrive")}>
                            <ListItemIcon><LinkOffIcon fontSize="small" color="error" /></ListItemIcon>
                            <ListItemText sx={{ color: "error.main" }}>Remove link from client</ListItemText>
                        </MenuItem>
                    ) : (
                        <MenuItem disabled>
                            <ListItemText
                                primaryTypographyProps={{ variant: "body2" }}
                                sx={{ color: "text.secondary" }}
                            >
                                Stored in OneDrive — manage it there
                            </ListItemText>
                        </MenuItem>
                    )
                ) : (
                    <>
                        <MenuItem onClick={(e) => handleActionClick(e, "rename")}>
                            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                            <ListItemText>Rename</ListItemText>
                        </MenuItem>
                        <MenuItem onClick={(e) => handleActionClick(e, "move")}>
                            <ListItemIcon><DriveFileMoveIcon fontSize="small" /></ListItemIcon>
                            <ListItemText>Move</ListItemText>
                        </MenuItem>
                        <MenuItem onClick={(e) => handleActionClick(e, "delete")}>
                            <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                            <ListItemText sx={{ color: "error.main" }}>Delete</ListItemText>
                        </MenuItem>
                    </>
                )}
            </Menu>
        </Box>
    );
}
